import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  CalendarDays, Plus, Search, Star, Tag, ChevronRight, Edit3, Trash2,
  Calendar, Clock, MapPin, Eye, EyeOff,
} from 'lucide-react';
import api from '../api';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import CategoryManager from '../components/CategoryManager';

const colorMap = {
  coral: 'bg-[#E5614C]/15 text-[#E5614C] border-[#E5614C]/30',
  teal: 'bg-[#5DCAB5]/15 text-[#5DCAB5] border-[#5DCAB5]/30',
  yellow: 'bg-[#FCD968]/15 text-[#FCD968] border-[#FCD968]/30',
};

export default function EventsList() {
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showCategories, setShowCategories] = useState(false);
  const navigate = useNavigate();

  const loadAll = async () => {
    setLoading(true);
    try {
      const [evRes, catRes] = await Promise.all([
        api.get('/events'),
        api.get('/event-categories'),
      ]);
      setEvents(evRes.data);
      setCategories(catRes.data);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete event “${title}”? This cannot be undone.`)) return;
    try {
      await api.delete(`/events/${id}`);
      toast.success('Event deleted');
      setEvents((evs) => evs.filter((e) => e.id !== id));
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const filtered = useMemo(() => {
    return events.filter((e) => {
      if (q && !(e.title.toLowerCase().includes(q.toLowerCase()) || e.location?.toLowerCase().includes(q.toLowerCase()))) return false;
      if (filterStatus !== 'all' && e.status !== filterStatus) return false;
      if (filterCategory !== 'all' && e.category_id !== filterCategory) return false;
      return true;
    });
  }, [events, q, filterStatus, filterCategory]);

  return (
    <div className="max-w-7xl space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="inline-flex items-center gap-2 text-[#E5614C] font-semibold uppercase tracking-[0.18em] text-[11px]">
            <CalendarDays className="h-3 w-3" /> Events
          </div>
          <h1 className="font-serif-display text-[36px] lg:text-[42px] font-bold mt-2">Manage events</h1>
          <p className="text-white/55 text-[14px] mt-1">Add, edit, and feature events shown across your site.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowCategories(true)} variant="ghost" className="h-10 rounded-full bg-white/5 border border-white/10 text-white/75 hover:bg-white/10 hover:text-white font-semibold px-5">
            <Tag className="h-4 w-4 mr-1.5" /> Categories
          </Button>
          <Button onClick={() => navigate('/admin/events/new')} className="h-10 rounded-full bg-[#E5614C] hover:bg-[#d04b36] text-white font-semibold px-5 btn-press shadow-[0_8px_20px_-6px_rgba(229,97,76,0.5)]">
            <Plus className="h-4 w-4 mr-1.5" /> New Event
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
          <Input
            value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search events…"
            className="h-11 pl-10 rounded-xl bg-[#16131a] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#E5614C]/40 focus-visible:border-[#E5614C]"
          />
        </div>
        <FilterChips
          options={[
            { value: 'all', label: 'All' },
            { value: 'published', label: 'Published' },
            { value: 'draft', label: 'Drafts' },
          ]}
          value={filterStatus}
          onChange={setFilterStatus}
        />
        <select
          value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}
          className="h-11 rounded-xl bg-[#16131a] border border-white/10 text-white text-[13px] px-3 focus:outline-none focus:ring-2 focus:ring-[#E5614C]/40"
        >
          <option value="all">All categories</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="rounded-2xl bg-[#16131a] border border-white/[0.06] overflow-hidden">
        {loading && <div className="p-10 text-center text-white/50 text-[14px]">Loading…</div>}
        {!loading && filtered.length === 0 && (
          <div className="p-14 text-center">
            <div className="h-14 w-14 rounded-2xl bg-[#E5614C]/15 text-[#E5614C] flex items-center justify-center mx-auto">
              <CalendarDays className="h-6 w-6" />
            </div>
            <div className="font-serif-display text-[22px] font-bold mt-4">No events yet</div>
            <p className="text-white/55 text-[14px] mt-1 max-w-sm mx-auto">Create your first event to start populating your site.</p>
            <Button onClick={() => navigate('/admin/events/new')} className="mt-5 rounded-full bg-[#E5614C] hover:bg-[#d04b36] text-white font-semibold h-10 px-5">
              <Plus className="h-4 w-4 mr-1.5" /> Create event
            </Button>
          </div>
        )}
        {filtered.map((ev, i) => {
          const catCls = ev.category ? colorMap[ev.category.color] || colorMap.coral : 'bg-white/8 text-white/65 border-white/10';
          return (
            <div
              key={ev.id}
              className={`group flex items-center gap-5 px-5 py-4 hover:bg-white/[0.04] transition-colors ${i < filtered.length - 1 ? 'border-b border-white/[0.06]' : ''}`}
            >
              <Link to={`/admin/events/${ev.id}`} className="flex items-center gap-4 flex-1 min-w-0">
                <div className="h-14 w-14 rounded-xl overflow-hidden bg-[#0f0d12] border border-white/[0.06] shrink-0">
                  {ev.featured_image ? (
                    <img src={ev.featured_image.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${ev.featured_image}` : ev.featured_image} alt={ev.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center text-white/30">
                      <CalendarDays className="h-5 w-5" />
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-serif-display font-bold text-[16px] truncate">{ev.title}</div>
                    {ev.is_featured && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-[0.14em] uppercase px-1.5 py-0.5 rounded bg-[#FCD968]/20 text-[#FCD968]">
                        <Star className="h-2.5 w-2.5" fill="currentColor" /> Featured
                      </span>
                    )}
                    {ev.status === 'draft' && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-[0.14em] uppercase px-1.5 py-0.5 rounded bg-white/10 text-white/55">
                        <EyeOff className="h-2.5 w-2.5" /> Draft
                      </span>
                    )}
                    {ev.category && (
                      <span className={`inline-flex items-center text-[10px] font-bold tracking-[0.14em] uppercase px-1.5 py-0.5 rounded border ${catCls}`}>
                        {ev.category.name}
                      </span>
                    )}
                  </div>
                  <div className="text-[12px] text-white/50 flex flex-wrap gap-x-4 gap-y-1 mt-1">
                    <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {formatDate(ev.start_date)}</span>
                    {ev.start_time && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {ev.start_time}{ev.end_time ? ` – ${ev.end_time}` : ''}</span>}
                    {ev.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {ev.location}</span>}
                  </div>
                </div>
              </Link>
              <div className="flex items-center gap-1 shrink-0">
                <Link to={`/admin/events/${ev.id}`} className="h-9 w-9 rounded-lg hover:bg-white/10 flex items-center justify-center text-white/65 hover:text-white transition-colors" title="Edit">
                  <Edit3 className="h-4 w-4" />
                </Link>
                <button onClick={() => handleDelete(ev.id, ev.title)} className="h-9 w-9 rounded-lg hover:bg-red-500/15 flex items-center justify-center text-white/55 hover:text-red-400 transition-colors" title="Delete">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {showCategories && (
        <CategoryManager
          categories={categories}
          onClose={() => setShowCategories(false)}
          onChange={loadAll}
        />
      )}
    </div>
  );
}

function FilterChips({ options, value, onChange }) {
  return (
    <div className="flex gap-1 bg-[#16131a] p-1 rounded-full border border-white/[0.06]">
      {options.map((o) => (
        <button
          key={o.value}
          onClick={() => onChange(o.value)}
          className={`px-3.5 h-9 rounded-full text-[12px] font-semibold transition-colors ${
            value === o.value ? 'bg-white text-[#0f0d12]' : 'text-white/55 hover:text-white'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function formatDate(d) {
  if (!d) return '';
  const dt = new Date(d + 'T00:00:00');
  return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}
